import { useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useCalendarEvents } from "@/hooks/use-calendar-events";
import { CalendarToolbar, type CalendarView } from "@/components/calendar/CalendarToolbar";
import { NewEventModal } from "@/components/calendar/NewEventModal";
import { usePrefs } from "@/lib/prefs-store";
import {
  addMonths,
  isoDate,
  isoMonth,
  isoWeek,
  isoYear,
  parseIsoMonth,
  weekDayHeaders,
} from "@/lib/calendar-utils";

type ContextMenu = {
  cell: Date;
  iso: string;
  x: number;
  y: number;
};

/**
 * BW10 — month view. Routes:
 *   /calendar              → current month
 *   /calendar/month/:m     → explicit YYYY-MM
 */

const VIEW: CalendarView = "month";
const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

function startOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

function buildGrid(monthStart: Date, weekStartsOn: 0 | 1 | 6): Date[] {
  const day = monthStart.getDay();
  const offset = (day - weekStartsOn + 7) % 7;
  const start = new Date(monthStart);
  start.setDate(start.getDate() - offset);
  const cells: Date[] = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    cells.push(d);
  }
  return cells;
}

export function Calendar() {
  const navigate = useNavigate();
  const { month } = useParams<{ month: string }>();
  const monthStart = month ? parseIsoMonth(month) : startOfMonth(new Date());
  const [showNew, setShowNew] = useState(false);
  const [seedStart, setSeedStart] = useState<Date | undefined>(undefined);
  const [contextMenu, setContextMenu] = useState<ContextMenu | null>(null);
  const longPressTimer = useRef<number | null>(null);
  const longPressFired = useRef(false);

  const weekStart = usePrefs((s) => s.weekStart);
  const today = useMemo(() => isoDate(new Date()), []);
  const cells = useMemo(() => buildGrid(monthStart, weekStart), [monthStart, weekStart]);
  const dow = useMemo(() => weekDayHeaders(weekStart), [weekStart]);
  const rangeStart = cells[0];
  const rangeEnd = new Date(cells[41]);
  rangeEnd.setDate(rangeEnd.getDate() + 1);

  const events = useCalendarEvents(rangeStart, rangeEnd);
  const countByDay = useMemo(() => {
    const map = new Map<string, number>();
    for (const e of events.data ?? []) {
      map.set(e.date, (map.get(e.date) ?? 0) + 1);
    }
    return map;
  }, [events.data]);

  function go(delta: number) {
    navigate(`/calendar/month/${isoMonth(addMonths(monthStart, delta))}`);
  }

  function onSwipeStart(e: React.TouchEvent) {
    (e.currentTarget as HTMLElement).dataset.startX = String(e.touches[0].clientX);
  }
  function onSwipeEnd(e: React.TouchEvent) {
    const startX = Number((e.currentTarget as HTMLElement).dataset.startX ?? 0);
    const dx = e.changedTouches[0].clientX - startX;
    if (Math.abs(dx) < 60) return;
    go(dx > 0 ? -1 : 1);
  }

  return (
    <article aria-labelledby="calendar-heading">
      <p
        style={{
          fontFamily: "var(--font-sans)",
          fontSize: "var(--type-micro)",
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          color: "var(--fg-3)",
          margin: 0,
        }}
      >
        Calendar · Month
      </p>
      <CalendarToolbar
        title={`${MONTHS[monthStart.getMonth()]} ${monthStart.getFullYear()}`}
        view={VIEW}
        isoDay={isoDate(new Date())}
        isoWeek={isoWeek(monthStart)}
        isoMonth={isoMonth(monthStart)}
        isoYear={isoYear(monthStart)}
        onPrev={() => go(-1)}
        onNext={() => go(1)}
        onToday={() => navigate(`/calendar/month/${isoMonth(new Date())}`)}
        onNewEvent={() => {
          setSeedStart(undefined);
          setShowNew(true);
        }}
      />
      <h2 id="calendar-heading" style={{ position: "absolute", left: -9999 }}>
        Calendar month
      </h2>

      <div
        onTouchStart={onSwipeStart}
        onTouchEnd={onSwipeEnd}
        style={{
          background: "var(--bg-2)",
          border: "1px solid var(--hair)",
          borderRadius: "var(--radius-card)",
          padding: "var(--space-2)",
          touchAction: "pan-y",
        }}
      >
        <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)" }}>
          {dow.map((d) => (
            <div
              key={d}
              style={{
                padding: "var(--space-2)",
                fontSize: "var(--type-meta)",
                color: "var(--fg-3)",
                textAlign: "center",
                letterSpacing: "0.06em",
                textTransform: "uppercase",
              }}
            >
              {d}
            </div>
          ))}
        </div>
        <div
          className="haus-cal-grid"
          style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 1 }}
        >
          <style>{`
            .haus-cal-cell {
              position: relative;
              aspect-ratio: 1 / 1;
              background: var(--bg-3);
              border-radius: var(--radius-chip);
              padding: 6;
              font-family: var(--font-mono);
              font-size: 13px;
              cursor: pointer;
              display: flex;
              flex-direction: column;
              align-items: flex-start;
              justify-content: space-between;
              text-align: left;
              transition: background var(--dur-fast) var(--ease-out);
              border: 1px solid transparent;
              user-select: none;
              -webkit-user-select: none;
              -webkit-tap-highlight-color: transparent;
            }
            .haus-cal-cell:hover {
              background: var(--chip);
            }
            .haus-cal-cell:hover .haus-cal-add {
              opacity: 1;
            }
            .haus-cal-cell.is-today {
              border-color: var(--accent);
            }
            .haus-cal-add {
              position: absolute;
              top: 4px;
              right: 4px;
              width: 16px;
              height: 16px;
              border-radius: 50%;
              background: var(--accent);
              color: #0a0b0d;
              display: inline-flex;
              align-items: center;
              justify-content: center;
              font-size: 12px;
              font-weight: 600;
              opacity: 0;
              transition: opacity var(--dur-fast) var(--ease-out);
              pointer-events: none;
            }
          `}</style>
          {cells.map((cell) => {
            const iso = isoDate(cell);
            const inMonth = cell.getMonth() === monthStart.getMonth();
            const count = countByDay.get(iso) ?? 0;
            const isToday = iso === today;
            return (
              <div
                key={iso}
                role="button"
                tabIndex={0}
                aria-label={`${cell.toDateString()} — ${count} event${count === 1 ? "" : "s"}`}
                className={`haus-cal-cell${isToday ? " is-today" : ""}`}
                style={{
                  color: inMonth ? "var(--fg)" : "var(--fg-4)",
                }}
                onClick={() => {
                  if (longPressFired.current) {
                    longPressFired.current = false;
                    return;
                  }
                  navigate(`/calendar/day/${iso}`);
                }}
                onDoubleClick={() => {
                  setSeedStart(cell);
                  setShowNew(true);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    navigate(`/calendar/day/${iso}`);
                  }
                }}
                onContextMenu={(e) => {
                  e.preventDefault();
                  setContextMenu({ cell, iso, x: e.clientX, y: e.clientY });
                }}
                onTouchStart={(e) => {
                  const t = e.touches[0];
                  const x = t.clientX;
                  const y = t.clientY;
                  if (longPressTimer.current != null) {
                    window.clearTimeout(longPressTimer.current);
                  }
                  longPressTimer.current = window.setTimeout(() => {
                    longPressFired.current = true;
                    setContextMenu({ cell, iso, x, y });
                  }, 480);
                }}
                onTouchEnd={() => {
                  if (longPressTimer.current != null) {
                    window.clearTimeout(longPressTimer.current);
                    longPressTimer.current = null;
                  }
                }}
                onTouchCancel={() => {
                  if (longPressTimer.current != null) {
                    window.clearTimeout(longPressTimer.current);
                    longPressTimer.current = null;
                  }
                }}
                onTouchMove={() => {
                  // Cancel long-press on movement so swipe-to-change-month wins.
                  if (longPressTimer.current != null) {
                    window.clearTimeout(longPressTimer.current);
                    longPressTimer.current = null;
                  }
                }}
              >
                <span aria-hidden className="haus-cal-add">
                  +
                </span>
                <span>{cell.getDate()}</span>
                {count > 0 && (
                  <span
                    aria-label={`${count} events`}
                    style={{
                      display: "flex",
                      gap: 3,
                      marginBottom: 2,
                      alignSelf: "center",
                    }}
                  >
                    {Array.from({ length: Math.min(count, 3) }).map((_, i) => (
                      <span
                        key={i}
                        aria-hidden
                        style={{
                          width: 4,
                          height: 4,
                          borderRadius: "50%",
                          background: "var(--accent)",
                        }}
                      />
                    ))}
                  </span>
                )}
              </div>
            );
          })}
        </div>

        {contextMenu && (
          <CalendarContextMenu
            menu={contextMenu}
            onClose={() => setContextMenu(null)}
            onAddEvent={() => {
              setSeedStart(contextMenu.cell);
              setShowNew(true);
              setContextMenu(null);
            }}
            onViewDay={() => {
              navigate(`/calendar/day/${contextMenu.iso}`);
              setContextMenu(null);
            }}
            onJumpToday={() => {
              navigate(`/calendar/month/${isoMonth(new Date())}`);
              setContextMenu(null);
            }}
          />
        )}
      </div>

      <p
        style={{
          marginTop: "var(--space-3)",
          color: "var(--fg-3)",
          fontSize: "var(--type-meta)",
          letterSpacing: "0.04em",
        }}
      >
        Shortcuts: ←/→ prev/next month · T today · D/W/Y switch view · N new event ·
        right-click a day to schedule.
      </p>

      <NewEventModal
        open={showNew}
        defaultStart={seedStart}
        onClose={() => setShowNew(false)}
      />
    </article>
  );
}

function CalendarContextMenu({
  menu,
  onClose,
  onAddEvent,
  onViewDay,
  onJumpToday,
}: {
  menu: ContextMenu;
  onClose: () => void;
  onAddEvent: () => void;
  onViewDay: () => void;
  onJumpToday: () => void;
}) {
  return (
    <div
      role="menu"
      aria-label="Day actions"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 40,
      }}
      onClick={onClose}
      onContextMenu={(e) => {
        e.preventDefault();
        onClose();
      }}
    >
      <ul
        role="presentation"
        style={{
          position: "absolute",
          top: Math.min(menu.y, window.innerHeight - 240),
          left: Math.min(menu.x, window.innerWidth - 220),
          minWidth: 200,
          background: "var(--bg-3)",
          border: "1px solid var(--hair-2)",
          borderRadius: "var(--radius-card)",
          boxShadow: "0 16px 40px rgba(0,0,0,0.45)",
          listStyle: "none",
          margin: 0,
          padding: "var(--space-2)",
          color: "var(--fg)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <MenuItem label="Add event" onClick={onAddEvent} />
        <MenuItem label="View day" onClick={onViewDay} />
        <MenuItem label="Jump to today" onClick={onJumpToday} />
      </ul>
    </div>
  );
}

function MenuItem({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <li>
      <button
        type="button"
        role="menuitem"
        onClick={onClick}
        style={{
          width: "100%",
          textAlign: "left",
          padding: "var(--space-2) var(--space-3)",
          background: "transparent",
          color: "inherit",
          border: "none",
          borderRadius: "var(--radius-chip)",
          fontSize: "var(--type-body-s)",
          cursor: "pointer",
        }}
        onMouseEnter={(e) => {
          (e.currentTarget as HTMLElement).style.background = "var(--chip)";
        }}
        onMouseLeave={(e) => {
          (e.currentTarget as HTMLElement).style.background = "transparent";
        }}
      >
        {label}
      </button>
    </li>
  );
}
